import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { useTheme } from "../../contexts/ThemeContext";
import { adminRoutes } from "../../routers/AdminRouters";
import {
  useCreateAdminMutation,
  useGetOrganisationsQuery,
} from "../../services";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { toast } from "../../core/toast";
import PhoneInputField from "../../components/PhoneInputField";
import { selectOrganisationId, setSelectedOrganisation } from "../../redux/organisationReducer";
import { useEffect } from "react";
import { useAppDispatch } from "../../redux/hooks";

const AdminCreate: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [createAdmin, { isLoading, error }] = useCreateAdminMutation();

  const {
    data: orgsData,
    isLoading: isOrgLoading,
    isError: isOrgError,
  } = useGetOrganisationsQuery();

  const organisationOptions: Array<any> =
    (orgsData as any)?.data ?? (orgsData as any) ?? [];

  // Map options for react-select and deduplicate
  const selectOptions = useMemo(() => {
    const unique = new Map();
    organisationOptions.forEach((org: any) => {
      const id = org.id ?? org._id;
      const label = org.name ?? org.title ?? id;
      if (id && !unique.has(id)) {
        unique.set(id, { value: id, label });
      }
    });
    return Array.from(unique.values());
  }, [organisationOptions]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organisation: [] as any[], // Changed to array for multi-select
    designation: "",
  });

  const organisationId = useAppSelector(selectOrganisationId);
  const dispatch = useAppDispatch();

  // Set default organization from Redux and sync when Redux changes
  useEffect(() => {
    if (organisationId && selectOptions.length > 0) {
      const currentSelection = form.organisation.map((o: any) => o.value);
      // Only sync if the current global ID is not already in the form selection (or if form is empty)
      if (!currentSelection.includes(organisationId)) {
        const defaultOrg = selectOptions.find((opt) => opt.value === organisationId);
        if (defaultOrg) {
          setForm((f) => ({ ...f, organisation: [defaultOrg] }));
        }
      }
    }
  }, [organisationId, selectOptions]);

  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
    organisation?: string;
    designation?: string;
  }>({});
  const emailRegex = /^\S+@\S+\.\S+$/;

  const cardClass =
    theme === "dark"
      ? "bg-[#0F0F0F] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const inputClassBase =
    "w-full rounded-md px-3 py-2 outline-none transition-colors";
  const inputClass =
    theme === "dark"
      ? `${inputClassBase} bg-[#111111] border border-[#2C2C2C] placeholder-gray-500 focus:border-[#B6CB24]`
      : `${inputClassBase} bg-white border border-gray-300 placeholder-gray-500 focus:border-[#B6CB24]`;

  const labelClass = theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700";

  // Custom styles for react-select
  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: theme === "dark" ? "#111111" : "white",
      borderColor: state.isFocused
        ? "#B6CB24"
        : errors.organisation
          ? "#ef4444"
          : theme === "dark"
            ? "#2C2C2C"
            : "#e5e7eb",
      color: theme === "dark" ? "white" : "#111827",
      minHeight: "42px",
      boxShadow: state.isFocused ? "0 0 0 1px #B6CB24" : null,
      "&:hover": {
        borderColor: errors.organisation ? "#ef4444" : "#B6CB24",
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: theme === "dark" ? "#1e1e1e" : "white",
      zIndex: 9999,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#B6CB24"
        : state.isFocused
          ? theme === "dark"
            ? "#333333"
            : "#f3f4f6"
          : "transparent",
      color: state.isSelected
        ? "black"
        : theme === "dark"
          ? "white"
          : "#111827",
      cursor: "pointer",
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: theme === "dark" ? "white" : "#111827",
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: theme === "dark" ? "#2C2C2C" : "#e5e7eb",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: theme === "dark" ? "white" : "#111827",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: theme === "dark" ? "white" : "#4b5563",
      ":hover": {
        backgroundColor: "#ef4444",
        color: "white",
      },
    }),
    input: (provided: any) => ({
      ...provided,
      color: theme === "dark" ? "white" : "#111827",
    }),
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSelectChange = (newValue: any) => {
    setForm((f) => ({ ...f, organisation: newValue }));

    // Reverse sync: Update global organization when selection changes
    if (Array.isArray(newValue) && newValue.length > 0) {
      // Pick the last selected or first one to be the "active" global org
      const lastSelected = newValue[newValue.length - 1]?.value;
      if (lastSelected && lastSelected !== organisationId) {
        dispatch(setSelectedOrganisation(lastSelected));
      }
    }

    if (errors.organisation) {
      setErrors((prev) => ({ ...prev, organisation: undefined }));
    }
  };

  const handleEmailBlur = () => {
    const email = form.email.trim();
    setErrors((prev) => ({
      ...prev,
      email: email && !emailRegex.test(email) ? "Email is invalid" : undefined,
    }));
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(form.email.trim())) {
      newErrors.email = "Email is invalid";
    }
    if (!form.phone.trim()) newErrors.phone = "Phone is required";
    if (!form.organisation || form.organisation.length === 0) {
      newErrors.organisation = "Assign at least one organisation";
    }
    if (!form.designation.trim()) {
      newErrors.designation = "Designation is required";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fill all mandatory fields marked with *");
    }
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: String(form.name || "").trim(),
      email: String(form.email || "").trim(),
      phone: String(form.phone || "").trim(),
      assignedOrganisations: form.organisation.map((org: any) => org.value),
      designation: String(form.designation || "").trim(),
    };

    try {
      await createAdmin(payload).unwrap();
      navigate(adminRoutes.adminList);
    } catch (e) {
      console.error("Create admin failed:", e);
    }
  };
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50"
        }`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Breadcrumb + Title */}
      <div className="mb-4">
        <h1
          className={`text-xl font-semibold ${theme === "dark" ? "text-gray-100" : "text-gray-900"
            }`}
        >
          Admin Overview
        </h1>
        <div className="text-sm mt-1">
          <Link
            to={`${adminRoutes.adminList}`}
            className={theme === "dark" ? "text-gray-400" : "text-gray-500"}
          >
            All Admins
          </Link>
          <span
            className={theme === "dark" ? "text-gray-600" : "text-gray-400"}
          >
            {" "}
            &nbsp;&gt;&nbsp;{" "}
          </span>
          <span className="text-[#B6CB24] font-medium ">Create Admin</span>
        </div>
      </div>

      {/* Card */}
      <form
        onSubmit={onSubmit}
        className={`${cardClass} rounded-lg p-4 md:p-6`}
      >
        <div className="mb-6">
          <h2
            className={`text-base font-medium border-b pb-2 border-gray-700 ${theme === "dark" ? "text-white" : "text-gray-800"
              }`}
          >
            Create New Admin
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Name <span className="text-red-500">*</span>
            </label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter here"
              className={`${inputClass} ${errors.name ? "border-red-500" : ""}`}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Email <span className="text-red-500">*</span>
            </label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              placeholder="Enter here"
              className={`${inputClass} ${errors.email ? "border border-red-500" : ""
                }`}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>
          <PhoneInputField
            label="Phone Number"
            required
            value={form.phone}
            onChange={(value) => {
              setForm((f) => ({ ...f, phone: value }));
              if (errors.phone)
                setErrors((prev) => ({ ...prev, phone: undefined }));
            }}
            error={errors.phone}
            theme={theme}
          />
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Assign to Organisation <span className="text-red-500">*</span>
            </label>
            <Select
              value={form.organisation}
              onChange={handleSelectChange}
              options={selectOptions}
              isMulti
              isLoading={isOrgLoading}
              isDisabled={isOrgLoading || isOrgError}
              styles={customStyles}
              placeholder={
                isOrgLoading
                  ? "Loading..."
                  : isOrgError
                    ? "Failed to load"
                    : "Choose from list"
              }
              classNamePrefix="react-select"
            />
            {errors.organisation && (
              <p className="text-red-500 text-xs mt-1">{errors.organisation}</p>
            )}
          </div>
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Designation <span className="text-red-500">*</span>
            </label>
            <input
              name="designation"
              value={form.designation}
              onChange={handleChange}
              placeholder="Enter designation"
              className={`${inputClass} ${errors.designation ? "border-red-500" : ""}`}
            />
            {errors.designation && (
              <p className="text-red-500 text-xs mt-1">{errors.designation}</p>
            )}
          </div>
        </div>

        {/* Divider */}
        <div
          className={`mt-4 h-px ${theme === "dark" ? "bg-[#2C2C2C]" : "bg-gray-200"
            }`}
        />

        {/* Actions */}
        <div className="mt-4 flex items-center gap-3">
          <button
            type="submit"
            disabled={isLoading}
            className={`px-8 py-1.5 rounded-md font-medium ${theme === "dark"
              ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`} style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
              }}
          >
            {isLoading ? "Creating..." : "Create"}
          </button>
          <Link
            to={`${adminRoutes.adminList}`}
            className={`px-8 py-1.5 rounded-md font-medium ${theme === "dark"
              ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
          >
            Not Now
          </Link>
        </div>
      </form>
    </div>
  );
};

export default AdminCreate;
